/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { TagProps } from '@coursera/cds-core';
import { Tag } from '@coursera/cds-core';
import { AIGenerateIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/product-card';

type ColorCodedPillTag = {
  text: string;
  tagProps?: Partial<TagProps>;
};

type Props = {
  pillTags: ColorCodedPillTag[];
};

const getTranslatedPillTagText = (text: string) => {
  switch (text.toLowerCase()) {
    case 'popular':
      return _t('Popular');
    case 'free trial':
      return _t('Free Trial');
    case 'ai skills':
      return _t('AI skills');
    case 'build toward a degree':
      return _t('Build toward a degree');
    case 'earn a degree':
      return _t('Earn a degree');
    case 'credit offered':
      return _t('Credit offered');
    default:
      return text;
  }
};

const getPillTagIcon = (text: string) => {
  switch (text.toLowerCase()) {
    case 'ai skills':
      return <AIGenerateIcon size="small" />;
    default:
      return undefined;
  }
};

const getPillTagProps = (pillTag: ColorCodedPillTag): Partial<TagProps> => {
  const { text, tagProps } = pillTag;
  const lowerText = text.toLowerCase();
  const translatedText = getTranslatedPillTagText(text);
  const icon = getPillTagIcon(text);

  const defaultTagProps: Partial<TagProps> = {
    variant: 'category',
    priority: 'secondary',
    color: 'default',
    icon,
    children: translatedText,
    ...tagProps,
  };

  if (lowerText.includes('free trial')) {
    return {
      ...defaultTagProps,
      priority: 'secondary',
      color: 'highlight1',
      variant: 'status',
    };
  }

  if (lowerText.includes('build toward a degree')) {
    return { ...defaultTagProps, priority: 'secondary', color: 'default' };
  }

  if (lowerText.includes('earn a degree') || lowerText.includes('credit offered')) {
    return { ...defaultTagProps, priority: 'secondary', color: 'default', children: translatedText };
  }

  return defaultTagProps;
};

const styles = {
  container: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-50);
  `,
};

/**
 * Renders a list of color-coded pill tags using CDS Tag components.
 * @param {ColorCodedPillTag[]} props.pillTags - A list of pill tags to render. If an item in the list
 * is an untranslated string, this component will attempt to automatically translate the text and color
 * code the tag based on the text.
 */
const LearningProductCardColorCodedPillTags = ({ pillTags }: Props) => {
  if (!pillTags || pillTags.length === 0) {
    return null;
  }

  return (
    <div css={styles.container}>
      {pillTags.map((pillTag) => {
        const { children, ...tagProps } = getPillTagProps(pillTag);
        return (
          <Tag key={pillTag.text} {...tagProps}>
            {children ?? pillTag.text}
          </Tag>
        );
      })}
    </div>
  );
};

export default LearningProductCardColorCodedPillTags;
